import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Disclaimer } from "@/components/shared";
import { CheckoutModal } from "@/components/CheckoutModal";
import { PROVINCES, PRIMARY_REASONS, PRICING, labelFor } from "@/lib/constants";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Check, Pencil, Bell, CreditCard, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const FEATURES = ["Full personalized roadmap", "AI Family Navigator", "Funding & tax-credit finder",
  "Document vault & task tracker", "School support checklist", "Monthly next steps"];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return "—"; } };

export default function Account() {
  const { user, subscription, isPaid, refresh } = useAuth();
  const [family, setFamily] = useState(null);
  const [child, setChild] = useState(null);
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState("monthly");
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [reminders, setReminders] = useState(user?.email_task_reminders !== false);
  const [savingPref, setSavingPref] = useState(false);

  useEffect(() => { setReminders(user?.email_task_reminders !== false); }, [user?.email_task_reminders]);

  useEffect(() => {
    (async () => {
      setFamily((await api.get("/family")).data);
      setChild((await api.get("/child-profile")).data);
    })();
  }, []);

  const toggleReminders = async (next) => {
    setReminders(next); setSavingPref(true);
    try {
      await api.patch("/user/email-preferences", { task_reminders: next });
      await refresh();
      toast.success(next ? "Weekly reminder emails turned on." : "Weekly reminder emails turned off.");
    } catch (e) { setReminders(!next); toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingPref(false); }
  };

  const startCheckout = (planInterval) => { setInterval(planInterval); setCheckoutOpen(true); };

  const onPaid = async () => { await refresh(); };

  const cancelPlan = async () => {
    setBusy(true);
    try { await api.post("/billing/cancel"); await refresh(); toast.success("Your plan will end at the current period. You keep access until then."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const resumePlan = async () => {
    setBusy(true);
    try { await api.post("/billing/resume"); await refresh(); toast.success("Your plan will continue — cancellation removed."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const periodEnd = subscription?.current_period_end;
  const cancelScheduled = subscription?.cancel_at_period_end;
  const planIntervalLabel = subscription?.plan_interval === "annual" ? "$169 CAD / year" : "$19 CAD / month";

  return (
    <div className="max-w-3xl kid-stagger">
      <PageHeader eyebrow="Account" title="Your account & plan" subtitle="Manage your plan, profile and details." />

      {/* Plan */}
      <div className="kid-card p-6 mb-5" data-testid="account-plan">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide font-poppins text-kid-muted">Current plan</div>
            <div className="text-2xl font-extrabold text-kid-navy mt-0.5">{isPaid ? "Family Path" : "Calm Start (Free)"}</div>
            <div className="text-sm text-kid-muted mt-0.5">{isPaid ? `${planIntervalLabel} · full guided experience` : "Starter roadmap & funding overview"}</div>
          </div>
          {isPaid && (
            <button onClick={openPortal} disabled={busy} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="account-portal">
              <CreditCard size={16} /> Manage billing
            </button>
          )}
        </div>

        {isPaid ? (
          <div className="mt-5 pt-5 border-t border-kid-line">
            {periodEnd && (
              <div className="flex items-center gap-2 text-sm text-kid-muted mb-4" data-testid="account-renewal">
                <CalendarClock size={16} className="text-kid-teal" />
                {cancelScheduled ? <span>Your plan ends on <b className="text-kid-navy">{fmtDate(periodEnd)}</b>.</span>
                                 : <span>Renews on <b className="text-kid-navy">{fmtDate(periodEnd)}</b>.</span>}
              </div>
            )}
            {cancelScheduled ? (
              <button onClick={resumePlan} disabled={busy} className="kid-btn kid-btn-primary h-11 px-5" data-testid="account-resume">
                {busy ? "…" : "Resume my plan"}
              </button>
            ) : (
              <button onClick={cancelPlan} disabled={busy} className="kid-btn kid-btn-ghost h-11 px-5 text-kid-coral" data-testid="account-cancel">
                {busy ? "…" : "Cancel plan"}
              </button>
            )}
          </div>
        ) : (
          <div className="mt-5 pt-5 border-t border-kid-line">
            <div className="grid sm:grid-cols-2 gap-2 mb-6">
              {FEATURES.map((f) => (
                <div key={f} className="flex items-center gap-2 text-sm text-kid-ink/80"><Check size={16} className="text-kid-teal" /> {f}</div>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <button onClick={() => startCheckout("monthly")} disabled={busy}
                className="text-left rounded-2xl border-2 border-kid-line hover:border-kid-teal p-5 transition-colors" data-testid="plan-monthly">
                <div className="text-sm font-poppins font-semibold text-kid-muted">Monthly</div>
                <div className="text-2xl font-extrabold text-kid-navy mt-1">${PRICING.monthly.amount}<span className="text-base font-semibold text-kid-muted"> CAD/mo</span></div>
                <div className="mt-3 inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm"><Sparkles size={15} /> Start monthly</div>
              </button>
              <button onClick={() => startCheckout("annual")} disabled={busy}
                className="text-left rounded-2xl border-2 border-kid-teal bg-kid-teal/5 p-5 relative transition-colors hover:bg-kid-teal/10" data-testid="plan-annual">
                <span className="absolute top-3 right-3 text-[.65rem] font-bold uppercase tracking-wide bg-kid-teal text-white px-2 py-0.5 rounded-full">Best value</span>
                <div className="text-sm font-poppins font-semibold text-kid-muted">Annual</div>
                <div className="text-2xl font-extrabold text-kid-navy mt-1">${PRICING.annual.amount}<span className="text-base font-semibold text-kid-muted"> CAD/yr</span></div>
                <div className="text-xs text-kid-teal font-semibold mt-0.5">{PRICING.annual.note}</div>
                <div className="mt-2 inline-flex items-center gap-1.5 text-kid-teal font-semibold text-sm"><Sparkles size={15} /> Start annual</div>
              </button>
            </div>
            <p className="text-xs text-kid-muted mt-4">Secure payment by Stripe. Cancel anytime. Have a promo code? You can enter it at checkout.</p>
          </div>
        )}
      </div>

      {/* Profile */}
      <div className="kid-card p-6 mb-5" data-testid="account-profile">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-kid-navy text-lg">Family & child profile</h3>
          <Link to="/app/onboarding" className="text-sm text-kid-teal font-poppins font-semibold inline-flex items-center gap-1" data-testid="account-edit-profile"><Pencil size={14} /> Edit</Link>
        </div>
        <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <div><dt className="text-kid-muted">Caregiver</dt><dd className="text-kid-navy font-medium">{family?.caregiver_first_name || "—"}</dd></div>
          <div><dt className="text-kid-muted">Province</dt><dd className="text-kid-navy font-medium">{family ? labelFor(PROVINCES, family.province) : "—"}</dd></div>
          <div><dt className="text-kid-muted">Reason for using KidPath</dt><dd className="text-kid-navy font-medium">{family ? labelFor(PRIMARY_REASONS, family.primary_reason) : "—"}</dd></div>
          <div><dt className="text-kid-muted">Child</dt><dd className="text-kid-navy font-medium">{child?.nickname || "—"}</dd></div>
        </dl>
      </div>

      {/* Account */}
      <div className="kid-card p-6 mb-5">
        <h3 className="font-bold text-kid-navy text-lg mb-4">Account details</h3>
        <dl className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
          <div><dt className="text-kid-muted">Name</dt><dd className="text-kid-navy font-medium">{user?.first_name}</dd></div>
          <div><dt className="text-kid-muted">Email</dt><dd className="text-kid-navy font-medium">{user?.email}</dd></div>
          <div><dt className="text-kid-muted">AI questions used this period</dt><dd className="text-kid-navy font-medium">{subscription?.ai_usage_month ?? 0} / {isPaid ? 200 : 5}</dd></div>
        </dl>
      </div>

      {/* Email reminders */}
      <div className="kid-card p-6 mb-5" data-testid="account-email-prefs">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-kid-teal/10 text-kid-teal grid place-items-center shrink-0"><Bell size={19} /></div>
            <div>
              <h3 className="font-bold text-kid-navy text-lg">Weekly reminder emails</h3>
              <p className="text-sm text-kid-muted max-w-md">Get a gentle Monday email listing the tasks due that week. You can turn this off any time.</p>
            </div>
          </div>
          <Switch checked={reminders} disabled={savingPref} onCheckedChange={toggleReminders} data-testid="account-reminders-toggle" />
        </div>
      </div>

      <Disclaimer>KidPath provides information and navigation, not medical, legal, or tax advice. Your information stays private and controlled by you.</Disclaimer>

      <CheckoutModal open={checkoutOpen} planInterval={interval} onClose={() => setCheckoutOpen(false)} onPaid={onPaid} />
    </div>
  );
}
